import { FC, ReactNode } from 'react';

import { getAudienceFillRateStyle } from 'utils/auction';
import { formatCost } from 'utils/clickme';
import { APPROXIMATION, NON_BREAKING_SPACE } from 'utils/constants/common';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import auctionCampaignForecastCardElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_forecast_card_element_shown';
import { Card, LoadingContextProvider, ProgressBar, Skeleton, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { TextWithTooltip } from 'components/common/TextWithTooltip/TextWithTooltip';

import { AuctionBuilderBidErrorCode, auctionSelector } from 'store/models/auctionData';
import { useSelector } from 'store/utils';

const TrlKeys = {
    title: 'AuctionBuilder.AuctionForecastCard.title',
    titleTooltip: 'AuctionBuilder.AuctionForecastCard.titleTooltip',
    audienceFillRate: 'AuctionBuilder.AuctionForecastCard.audienceFillRate',
    audience: 'AuctionBuilder.AuctionForecastCard.audience',
    audienceTooltip: 'AuctionBuilder.AuctionForecastCard.audienceTooltip',
    from: 'AuctionBuilder.AuctionForecastCard.from',
    incorrectBid: 'AuctionBuilder.AuctionForecastCard.incorrectBid',
    noData: 'AuctionBuilder.AuctionForecastCard.noData',
};

const AuctionForecastCardWrapper: FC<{
    children: ReactNode;
    vacancyId?: string | null;
    campaignId?: string;
    audienceFillRate?: number;
    audiencePerWeek?: number;
    audienceCapacityPerWeek?: number;
}> = ({ children, vacancyId, campaignId, audiencePerWeek, audienceCapacityPerWeek }) => (
    <ElementShownAnchor
        fn={auctionCampaignForecastCardElementShown}
        vacancyId={vacancyId}
        campaignId={campaignId}
        audiencePerWeek={audiencePerWeek || undefined}
        audienceCapacityPerWeek={audienceCapacityPerWeek || undefined}
    >
        <Card
            padding={24}
            borderRadius={16}
            stretched
            borderStyle="phantom"
            borderWidth="default"
            data-qa="forecast-card"
        >
            {children}
        </Card>
        <VSpacing default={20} />
    </ElementShownAnchor>
);

export const AuctionForecastCard: FC<{ loading: boolean }> = ({ loading }) => {
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;
    const bidRating = useSelector(auctionSelector.bidRating);
    const errorCode = useSelector(auctionSelector.errors.bid);

    const audience = bidRating?.audience;

    const trls = useTrls();

    const header = (
        <>
            <TextWithTooltip
                textNode={
                    <Text style="primary" typography="title-5-semibold">
                        {trls(TrlKeys.title)}
                    </Text>
                }
                textValue={trls(TrlKeys.title)}
                tooltipContent={trls(TrlKeys.titleTooltip)}
            />
            <VSpacing default={16} />
        </>
    );

    if (!audience) {
        return (
            <AuctionForecastCardWrapper vacancyId={vacancyId} campaignId={campaignId}>
                {header}
                <Text style="secondary" typography="label-3-regular">
                    {trls(TrlKeys.noData)}
                </Text>
            </AuctionForecastCardWrapper>
        );
    }

    const { audienceFillRate, audiencePerWeek, audienceCapacityPerWeek } = audience;

    return (
        <AuctionForecastCardWrapper
            vacancyId={vacancyId}
            campaignId={campaignId}
            audienceFillRate={audienceFillRate}
            audiencePerWeek={audiencePerWeek}
            audienceCapacityPerWeek={audienceCapacityPerWeek}
        >
            {header}
            {errorCode &&
            [AuctionBuilderBidErrorCode.RangeUnderflow, AuctionBuilderBidErrorCode.ValueMissing].includes(errorCode) ? (
                <Text style="negative" typography="label-3-regular">
                    {trls(TrlKeys.incorrectBid)}
                </Text>
            ) : (
                <LoadingContextProvider loading={loading}>
                    {!!audienceFillRate && (
                        <>
                            <Skeleton height={20} width={228} borderRadius={8}>
                                <Text style={getAudienceFillRateStyle(audienceFillRate)} typography="label-3-regular">
                                    {trls(TrlKeys.audienceFillRate, [Math.round(audienceFillRate)])}
                                </Text>
                            </Skeleton>
                            <VSpacing default={12} />
                            <Skeleton height={8} borderRadius={8}>
                                <ProgressBar
                                    max={100}
                                    progress={audienceFillRate}
                                    style={getAudienceFillRateStyle(audienceFillRate)}
                                />
                            </Skeleton>
                        </>
                    )}
                    <VSpacing default={24} />
                    <TextWithTooltip
                        textNode={
                            <Text style="secondary" typography="label-2-regular">
                                {trls(TrlKeys.audience)}
                            </Text>
                        }
                        textValue={trls(TrlKeys.audience)}
                        tooltipContent={trls(TrlKeys.audienceTooltip)}
                    />
                    <VSpacing default={8} />
                    {audiencePerWeek && audienceCapacityPerWeek && (
                        <Skeleton height={21} width={150} borderRadius={8}>
                            <Text Element="span" typography="title-5-semibold">
                                {APPROXIMATION}
                                {formatCost(audiencePerWeek, NON_BREAKING_SPACE)}
                            </Text>
                            {NON_BREAKING_SPACE}
                            <Text Element="span" typography="label-1-regular" style="secondary">
                                {trls(TrlKeys.from)}
                                {NON_BREAKING_SPACE}
                                {formatCost(audienceCapacityPerWeek, NON_BREAKING_SPACE)}
                            </Text>
                        </Skeleton>
                    )}
                </LoadingContextProvider>
            )}
        </AuctionForecastCardWrapper>
    );
};
